/**
 * SearchMockup.tsx  (APX Intelligence — Live AI)
 * Connects to the real LLM via tRPC intelligence.query mutation.
 * Maintains full conversation history for multi-turn context.
 * Role-aware: passes userName, userRole, and callerEmail to the backend.
 * Access: Bookkeeper Supervisor and above only.
 * Share with Client: human-in-the-loop confirm-send dialog.
 */

import { useState, useRef, useEffect } from "react";
import React from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { Sparkles, Send, FileText, MessageSquare, Shield, AlertCircle, Loader2, RotateCcw, Clock, Users, TrendingUp, BookOpen, CheckSquare, BarChart2, AlertTriangle, Zap, Mail, Search, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { Streamdown } from "streamdown";

// ─── Role-based suggested queries ────────────────────────────────────────────
type SuggestedQuery = { icon: React.ElementType; text: string; label: string };

const ROLE_QUERIES: Record<string, SuggestedQuery[]> = {
  bookkeeper_supervisor: [
    { icon: BarChart2,    text: "Show me P&L summary for all clients this month.",                    label: "P&L Summary" },
    { icon: TrendingUp,   text: "Which clients had the highest expenses last month?",                  label: "Top Expenses" },
    { icon: AlertTriangle, text: "Which clients have anomalies flagged this period?",                  label: "Anomalies" },
    { icon: Users,        text: "Show me the current bookkeeper assignments for all clients.",         label: "Team" },
  ],
  controller: [
    { icon: Clock,       text: "Which clients are overdue for month-end close?",                     label: "Month-End" },
    { icon: FileText,    text: "What is the current status of P&L reviews awaiting my sign-off?",   label: "P&L Review" },
    { icon: TrendingUp,  text: "Which clients are flagged as high-priority this month?",             label: "Priority" },
    { icon: Users,       text: "Show me the current bookkeeper assignments for all clients.",        label: "Team" },
  ],
  bookkeeping_manager: [
    { icon: Users,       text: "Show me the full bookkeeper workload distribution right now.",       label: "Workload" },
    { icon: Clock,       text: "Which clients are overdue for month-end close this cycle?",          label: "Month-End" },
    { icon: BarChart2,   text: "Which bookkeepers have the most overdue tasks this week?",           label: "Performance" },
    { icon: AlertTriangle, text: "Are there any clients without a bookkeeper assigned?",             label: "Unassigned" },
  ],
  tax_division: [
    { icon: FileText,    text: "Which clients have P&L reports ready for review?",                  label: "P&L Ready" },
    { icon: BookOpen,    text: "What documents are outstanding for active tax engagements?",         label: "Documents" },
    { icon: Clock,       text: "Which client files are past their expected completion date?",        label: "Overdue" },
    { icon: Users,       text: "Who is the Controller assigned to a specific client?",              label: "Contacts" },
  ],
  admin: [
    { icon: Users,       text: "Give me a full summary of the current team structure and roles.",    label: "Team" },
    { icon: BarChart2,   text: "How many clients are currently active in the system?",               label: "Clients" },
    { icon: TrendingUp,  text: "What workflows are currently in progress across all teams?",         label: "Operations" },
    { icon: AlertTriangle, text: "Are there any system alerts or unresolved flags right now?",      label: "Alerts" },
  ],
};

const DEFAULT_QUERIES: SuggestedQuery[] = [
  { icon: Clock,       text: "Which clients are overdue for month-end close?",                     label: "Month-End" },
  { icon: Users,       text: "Show me the current bookkeeper assignments for all clients.",        label: "Team" },
  { icon: TrendingUp,  text: "Which clients are flagged as high-priority this month?",             label: "Priority" },
  { icon: FileText,    text: "What is the current status of P&L reviews awaiting Controller sign-off?", label: "P&L Review" },
];

// ─── Types ────────────────────────────────────────────────────────────────────
interface Message {
  id: string;
  role: "user" | "assistant";
  text: string;
  timestamp: Date;
  isError?: boolean;
}

// ─── Role display helpers ─────────────────────────────────────────────────────
const ROLE_DISPLAY: Record<string, string> = {
  super_admin: "Director",
  admin: "Director",
  controller: "Controller",
  bookkeeping_manager: "Bkkpr Mgr",
  bookkeeper_supervisor: "Bkkpr",
  bookkeeper: "Bkkpr",
  tax_division: "Tax Division",
};

// Roles that can access APX Intelligence
const INTELLIGENCE_ROLES = ["super_admin", "admin", "controller", "bookkeeping_manager", "tax_division"];

// ─── Share with Client Dialog ─────────────────────────────────────────────────
interface ShareDialogProps {
  aiResponse: string;
  senderName: string;
  senderEmail: string;
  onClose: () => void;
}

function ShareWithClientDialog({ aiResponse, senderName, senderEmail, onClose }: ShareDialogProps) {
  const [sendMode, setSendMode] = useState<"sms" | "email">("sms");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [subject, setSubject] = useState(() => `APX Financial Update — ${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}`);
  const [body, setBody] = useState(() => {
    const lines = aiResponse.split("\n").filter(l => l.trim());
    const summary = lines.slice(0, 8).join("\n");
    return sendMode === "sms"
      ? summary.substring(0, 1200)
      : `Dear Client,\n\nPlease find below a brief financial update from APX:\n\n${summary}\n\nPlease do not hesitate to reach out with any questions.\n\nWarm regards,\nAPX Team`;
  });
  const [isSending, setIsSending] = useState(false);
  const { data: clients } = trpc.clientStatus.listAll.useQuery();
  const [clientSearch, setClientSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const filteredClients = (clients ?? []).filter(c =>
    c.clientName.toLowerCase().includes(clientSearch.toLowerCase()) && clientSearch.length > 0
  ).slice(0, 8);

  // When mode changes, reformat body
  const handleModeChange = (mode: "sms" | "email") => {
    setSendMode(mode);
    const lines = aiResponse.split("\n").filter(l => l.trim());
    const summary = lines.slice(0, 8).join("\n");
    if (mode === "sms") {
      setBody(summary.substring(0, 1200));
    } else {
      setBody(`Dear Client,\n\nPlease find below a brief financial update from APX:\n\n${summary}\n\nPlease do not hesitate to reach out with any questions.\n\nWarm regards,\nAPX Team`);
    }
  };

  const shareWithClientMutation = trpc.intelligence.shareWithClient.useMutation({
    onSuccess: () => {
      toast.success(`Summary sent to ${clientName} via ${sendMode === "sms" ? "text" : "email"}`);
      onClose();
    },
    onError: (err) => {
      toast.error(`Send failed: ${err.message}`);
      setIsSending(false);
    },
  });

  const handleSend = () => {
    if (!clientName.trim()) { toast.error("Please select a client."); return; }
    if (sendMode === "sms" && !clientPhone.trim()) { toast.error("Client phone number is required for SMS."); return; }
    if (sendMode === "email" && !clientEmail.trim()) { toast.error("Client email is required."); return; }
    if (!body.trim()) { toast.error("Message body cannot be empty."); return; }
    setIsSending(true);
    shareWithClientMutation.mutate({
      callerEmail: senderEmail,
      callerName: senderName,
      clientName: clientName.trim(),
      sendMode,
      clientPhone: sendMode === "sms" ? clientPhone.trim() : undefined,
      clientEmail: sendMode === "email" ? clientEmail.trim() : undefined,
      subject: subject.trim(),
      body: body.trim(),
    });
  };

  const inputStyle = { background: "oklch(0.93 0.008 240)", border: "1px solid oklch(0.78 0.02 240)", color: "oklch(0.08 0.01 240)" };
  const labelStyle = { color: "oklch(0.14 0.04 240)" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl mx-4"
        style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.82 0.02 240)" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "oklch(0.82 0.02 240)" }}>
          <div className="flex items-center gap-2">
            <Share2 size={16} style={{ color: "oklch(0.08 0.01 240)" }} />
            <h2 className="text-base font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>Share with Client</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors hover:bg-black/5">
            <X size={16} style={{ color: "oklch(0.14 0.04 240)" }} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Text / Email toggle — SMS is primary */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={labelStyle}>Send via</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleModeChange("sms")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={sendMode === "sms"
                  ? { background: "oklch(0.12 0.04 240)", color: "oklch(0.97 0.005 240)" }
                  : { background: "oklch(0.93 0.008 240)", color: "oklch(0.14 0.04 240)", border: "1px solid oklch(0.78 0.02 240)" }}
              >
                <MessageSquare size={14} />
                Text (SMS)
              </button>
              <button
                onClick={() => handleModeChange("email")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={sendMode === "email"
                  ? { background: "oklch(0.12 0.04 240)", color: "oklch(0.97 0.005 240)" }
                  : { background: "oklch(0.93 0.008 240)", color: "oklch(0.14 0.04 240)", border: "1px solid oklch(0.78 0.02 240)" }}
              >
                <Mail size={14} />
                Email
              </button>
            </div>
          </div>

          {/* Notice */}
          <div className="px-4 py-2.5 rounded-xl text-sm flex items-start gap-2" style={{ background: "oklch(0.93 0.04 75 / 30%)", border: "1px solid oklch(0.82 0.06 75 / 40%)", color: "oklch(0.28 0.06 75)" }}>
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>
              {sendMode === "sms"
                ? "Review the text message below before sending. SMS is limited to 1,200 characters."
                : "Review the email below before sending. You can edit any part before confirming."}
            </span>
          </div>

          {/* Client selector */}
          <div className="relative">
            <label className="block text-sm font-semibold mb-1.5" style={labelStyle}>Client Name</label>
            <input
              value={clientSearch || clientName}
              onChange={e => { setClientSearch(e.target.value); setClientName(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Search or type client name..."
              className="w-full px-3 py-2 rounded-xl text-sm outline-none"
              style={inputStyle}
            />
            {showDropdown && filteredClients.length > 0 && (
              <div className="absolute z-10 w-full mt-1 rounded-xl shadow-lg overflow-hidden" style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.82 0.02 240)" }}>
                {filteredClients.map(c => (
                  <button
                    key={c.id}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 transition-colors"
                    style={{ color: "oklch(0.08 0.01 240)" }}
                    onMouseDown={() => {
                      setClientName(c.clientName);
                      setClientSearch("");
                      if (c.clientPhone) setClientPhone(c.clientPhone);
                      if (c.clientEmail) setClientEmail(c.clientEmail);
                      setShowDropdown(false);
                    }}
                  >
                    <span className="font-medium">{c.clientName}</span>
                    {c.clientPhone && <span className="ml-2 opacity-50 text-xs">{c.clientPhone}</span>}
                    {c.clientEmail && !c.clientPhone && <span className="ml-2 opacity-50 text-xs">{c.clientEmail}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Phone (SMS mode) */}
          {sendMode === "sms" && (
            <div>
              <label className="block text-sm font-semibold mb-1.5" style={labelStyle}>Client Phone Number</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={e => setClientPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                style={inputStyle}
              />
            </div>
          )}

          {/* Email (email mode) */}
          {sendMode === "email" && (
            <>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={labelStyle}>Client Email</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={e => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={labelStyle}>Subject</label>
                <input
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                  style={inputStyle}
                />
              </div>
            </>
          )}

          {/* Message body */}
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={labelStyle}>
              {sendMode === "sms" ? "Text Message" : "Message Body"}
              {sendMode === "sms" && <span className="ml-2 font-normal text-xs opacity-50">{body.length}/1200 chars</span>}
            </label>
            <textarea
              value={body}
              onChange={e => setBody(sendMode === "sms" ? e.target.value.substring(0, 1200) : e.target.value)}
              rows={sendMode === "sms" ? 6 : 10}
              className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-none"
              style={inputStyle}
            />
          </div>

          {/* Sender info */}
          <p className="text-xs" style={{ color: "oklch(0.45 0.02 240)" }}>
            {sendMode === "sms"
              ? <>Sending from: <strong>APX centralized number</strong> &nbsp;·&nbsp; On behalf of: {senderName}</>
              : <>Sending from: <strong>{senderEmail}</strong> (centralized APX outbound) &nbsp;·&nbsp; On behalf of: {senderName}</>}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t" style={{ borderColor: "oklch(0.82 0.02 240)" }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-medium transition-colors hover:bg-black/5" style={{ color: "oklch(0.14 0.04 240)" }}>
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || !clientName.trim() || (sendMode === "sms" ? !clientPhone.trim() : !clientEmail.trim())}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-40"
            style={{ background: "oklch(0.12 0.04 240)", color: "oklch(0.97 0.005 240)" }}
          >
            {isSending ? <Loader2 size={14} className="animate-spin" /> : sendMode === "sms" ? <MessageSquare size={14} /> : <Send size={14} />}
            {isSending ? "Sending…" : sendMode === "sms" ? "Confirm & Text" : "Confirm & Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SearchMockup() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [quickActionResult, setQuickActionResult] = useState<{ type: string; content: string } | null>(null);
  const [anomalyForm, setAnomalyForm] = useState({ clientName: "", description: "", show: false });
  const [emailForm, setEmailForm] = useState({ clientName: "", subject: "", context: "", show: false });

  // Share with Client dialog state
  const [shareDialog, setShareDialog] = useState<{ show: boolean; text: string }>({ show: false, text: "" });

  const tierDigestMutation = trpc.intelligence.tierDigest.useMutation({
    onSuccess: (data) => {
      setQuickActionResult({ type: "Tier Task Digest", content: data.digest });
    },
    onError: (err) => toast.error(`Digest failed: ${err.message}`),
  });

  const explainAnomalyMutation = trpc.intelligence.explainAnomaly.useMutation({
    onSuccess: (data) => {
      setQuickActionResult({ type: "Anomaly Explanation", content: data.explanation });
      setAnomalyForm(f => ({ ...f, show: false }));
    },
    onError: (err) => toast.error(`Explanation failed: ${err.message}`),
  });

  const draftEmailMutation = trpc.intelligence.draftEmail.useMutation({
    onSuccess: (data) => {
      setQuickActionResult({ type: "Email Draft", content: data.draft });
      setEmailForm(f => ({ ...f, show: false }));
    },
    onError: (err) => toast.error(`Draft failed: ${err.message}`),
  });

  const queryMutation = trpc.intelligence.query.useMutation({
    onSuccess: (data) => {
      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        text: typeof data.answer === "string" ? data.answer : String(data.answer),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    },
    onError: (err) => {
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        text: `I encountered an error: ${err.message}. Please try again.`,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMsg]);
    },
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, queryMutation.isPending]);

  const handleSend = (query: string) => {
    if (!query.trim() || queryMutation.isPending || !user) return;
    setShowSuggestions(false);

    const userMsg: Message = {
      id: Date.now().toString(),
      role: "user",
      text: query,
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");

    const history = updatedMessages.slice(-10).map((m) => ({
      role: m.role,
      content: m.text,
    }));

    queryMutation.mutate({
      question: query,
      userRole: ROLE_DISPLAY[user.role] ?? user.role,
      userName: user.name,
      callerEmail: user.email,
      conversationHistory: history.slice(0, -1),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setInput("");
    setShowSuggestions(true);
    inputRef.current?.focus();
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const roleDisplay = ROLE_DISPLAY[user?.role ?? ""] ?? user?.role ?? "";
  const suggestedQueries = ROLE_QUERIES[user?.role ?? ""] ?? DEFAULT_QUERIES;
  const canShareWithClient = user && INTELLIGENCE_ROLES.includes(user.role);

  // Access gate: show a clear message for bookkeepers
  if (user && !INTELLIGENCE_ROLES.includes(user.role)) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "oklch(0.90 0.008 240)" }}>
            <Shield size={22} style={{ color: "oklch(0.08 0.01 240)" }} />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "oklch(0.08 0.01 240)" }}>Access Restricted</h2>
          <p className="text-sm" style={{ color: "oklch(0.38 0.02 240)" }}>
            APX Intelligence is available to Bookkeeper Supervisor and above. Please contact your manager if you believe you need access.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col" style={{ minHeight: "calc(100vh - 120px)" }}>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "oklch(0.90 0.008 240)" }}
            >
              <Sparkles size={18} style={{ color: "oklch(0.08 0.01 240)" }} />
            </div>
            <div>
              <h1
                className="text-xl font-bold"
                style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.12 0.04 240)" }}
              >
                APX Intelligence
              </h1>
              <p className="text-sm" style={{ color: "oklch(0.14 0.04 240)" }}>
                AI-powered assistant with live APX operations &amp; financial data
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {messages.length > 0 && (
                <button
                  onClick={handleNewConversation}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-all duration-150"
                  style={{ background: "oklch(0.92 0.008 240)", color: "oklch(0.08 0.01 240)", border: "1px solid oklch(0.78 0.02 240)" }}
                  title="Clear conversation and start fresh"
                >
                  <RotateCcw size={11} />
                  New Conversation
                </button>
              )}
              <div
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold"
                style={{ background: "oklch(0.90 0.008 240)", color: "oklch(0.08 0.01 240)", border: "1px solid oklch(0.78 0.02 240)" }}
              >
                <Shield size={11} />
                {roleDisplay}
              </div>
            </div>
          </div>


        </div>

        {/* Conversation area */}
        <div className="flex-1 overflow-y-auto space-y-4 pb-4">

          {/* Suggested queries */}
          {showSuggestions && messages.length === 0 && (
            <div className="py-6 space-y-5">
              <div className="text-center">
                <p className="text-base font-semibold" style={{ color: "oklch(0.14 0.04 240)" }}>
                  What would you like to know?
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {suggestedQueries.map((q) => (
                  <button
                    key={q.text}
                    onClick={() => handleSend(q.text)}
                    className="flex flex-col gap-3 p-5 rounded-2xl text-left transition-all duration-150"
                    style={{
                      background: "oklch(0.95 0.006 240)",
                      border: "1px solid oklch(0 0 0 / 12%)",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = "oklch(0.93 0.008 240)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = "oklch(0.95 0.006 240)";
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: "oklch(0.90 0.008 240)" }}
                      >
                        <q.icon size={20} style={{ color: "oklch(0.08 0.01 240)" }} />
                      </div>
                      <span className="text-sm font-bold uppercase tracking-widest" style={{ color: "oklch(0.08 0.01 240)" }}>
                        {q.label}
                      </span>
                    </div>
                    <p className="text-base font-medium leading-snug" style={{ color: "oklch(0.14 0.04 240)" }}>
                      {q.text}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Claude Quick Actions — visible to manager/admin in empty state */}
          {showSuggestions && messages.length === 0 && (user?.role === "bookkeeping_manager" || user?.role === "admin" || user?.role === "super_admin" || user?.role === "controller") && (
            <div className="mt-2 mb-4">
              <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: "oklch(0.14 0.04 240)" }}>Quick Actions</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Tier Digest */}
                <button
                  onClick={() => tierDigestMutation.mutate({ callerEmail: user?.email ?? "" })}
                  disabled={tierDigestMutation.isPending}
                  className="flex flex-col gap-2 p-4 rounded-xl text-left transition-all duration-150 disabled:opacity-50"
                  style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.82 0.02 240)" }}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.90 0.01 240)" }}>
                    {tierDigestMutation.isPending ? <Loader2 size={16} className="animate-spin" style={{ color: "oklch(0.08 0.01 240)" }} /> : <Zap size={16} style={{ color: "oklch(0.08 0.01 240)" }} />}
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>Tier Digest</p>
                    <p className="text-sm mt-0.5" style={{ color: "oklch(0.14 0.04 240)" }}>Weekly bookkeeper team progress summary</p>
                  </div>
                </button>

                {/* Anomaly Explainer */}
                <button
                  onClick={() => setAnomalyForm(f => ({ ...f, show: true }))}
                  className="flex flex-col gap-2 p-4 rounded-xl text-left transition-all duration-150"
                  style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.82 0.02 240)" }}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.90 0.01 240)" }}>
                    <Search size={16} style={{ color: "oklch(0.08 0.01 240)" }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>Anomaly Explainer</p>
                    <p className="text-sm mt-0.5" style={{ color: "oklch(0.14 0.04 240)" }}>Plain-English explanation of a flagged item</p>
                  </div>
                </button>

                {/* Email Draft */}
                <button
                  onClick={() => setEmailForm(f => ({ ...f, show: true }))}
                  className="flex flex-col gap-2 p-4 rounded-xl text-left transition-all duration-150"
                  style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.82 0.02 240)" }}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.90 0.01 240)" }}>
                    <Mail size={16} style={{ color: "oklch(0.08 0.01 240)" }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>Email Draft</p>
                    <p className="text-sm mt-0.5" style={{ color: "oklch(0.14 0.04 240)" }}>Draft a client follow-up email</p>
                  </div>
                </button>
              </div>

              {/* Anomaly Form */}
              {anomalyForm.show && (
                <div className="mt-3 p-4 rounded-xl space-y-3" style={{ background: "oklch(0.95 0.006 240)", border: "1px solid oklch(0 0 0 / 12%)" }}>
                  <p className="text-sm font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>Anomaly Explainer</p>
                  <input value={anomalyForm.clientName} onChange={e => setAnomalyForm(f => ({ ...f, clientName: e.target.value }))} placeholder="Client name" className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm outline-none" style={{ borderColor: "oklch(0.75 0.02 240)", color: "oklch(0.12 0.04 240)" }} />
                  <textarea value={anomalyForm.description} onChange={e => setAnomalyForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the anomaly (e.g. large unexplained debit on 3/15)" rows={2} className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm outline-none resize-none" style={{ borderColor: "oklch(0.75 0.02 240)", color: "oklch(0.12 0.04 240)" }} />
                  <div className="flex gap-2">
                    <button onClick={() => explainAnomalyMutation.mutate({ clientName: anomalyForm.clientName, anomalyDescription: anomalyForm.description })} disabled={!anomalyForm.clientName || !anomalyForm.description || explainAnomalyMutation.isPending} className="px-4 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-40" style={{ background: "oklch(0.12 0.04 240)", color: "oklch(0.97 0.005 240)" }}>
                      {explainAnomalyMutation.isPending ? "Analyzing..." : "Explain"}
                    </button>
                    <button onClick={() => setAnomalyForm(f => ({ ...f, show: false }))} className="px-4 py-1.5 rounded-lg text-sm" style={{ color: "oklch(0.14 0.04 240)" }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Email Form */}
              {emailForm.show && (
                <div className="mt-3 p-4 rounded-xl space-y-3" style={{ background: "oklch(0.95 0.006 240)", border: "1px solid oklch(0 0 0 / 12%)" }}>
                  <p className="text-sm font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>Email Draft</p>
                  <input value={emailForm.clientName} onChange={e => setEmailForm(f => ({ ...f, clientName: e.target.value }))} placeholder="Client name" className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm outline-none" style={{ borderColor: "oklch(0.75 0.02 240)", color: "oklch(0.12 0.04 240)" }} />
                  <input value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} placeholder="Subject / reason for email" className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm outline-none" style={{ borderColor: "oklch(0.75 0.02 240)", color: "oklch(0.12 0.04 240)" }} />
                  <textarea value={emailForm.context} onChange={e => setEmailForm(f => ({ ...f, context: e.target.value }))} placeholder="Context (e.g. missing bank statement, overdue invoice)" rows={2} className="w-full bg-transparent border rounded-lg px-3 py-2 text-sm outline-none resize-none" style={{ borderColor: "oklch(0.75 0.02 240)", color: "oklch(0.12 0.04 240)" }} />
                  <div className="flex gap-2">
                    <button onClick={() => draftEmailMutation.mutate({ clientName: emailForm.clientName, subject: emailForm.subject, context: emailForm.context })} disabled={!emailForm.clientName || !emailForm.subject || !emailForm.context || draftEmailMutation.isPending} className="px-4 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-40" style={{ background: "oklch(0.12 0.04 240)", color: "oklch(0.97 0.005 240)" }}>
                      {draftEmailMutation.isPending ? "Drafting..." : "Draft Email"}
                    </button>
                    <button onClick={() => setEmailForm(f => ({ ...f, show: false }))} className="px-4 py-1.5 rounded-lg text-sm" style={{ color: "oklch(0.14 0.04 240)" }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Quick Action Result Panel */}
              {quickActionResult && (
                <div className="mt-3 p-4 rounded-xl" style={{ background: "oklch(0.95 0.006 240)", border: "1px solid oklch(0.92 0.005 240 / 25%)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold" style={{ color: "oklch(0.08 0.01 240)" }}>{quickActionResult.type}</p>
                    <div className="flex gap-2">
                      <button onClick={() => { navigator.clipboard.writeText(quickActionResult.content); toast.success("Copied to clipboard"); }} className="text-sm px-2 py-1 rounded" style={{ color: "oklch(0.08 0.01 240)", background: "oklch(0.97 0.01 220)" }}>Copy</button>
                      <button onClick={() => setQuickActionResult(null)} className="text-sm px-2 py-1 rounded" style={{ color: "oklch(0.08 0.01 240)", background: "oklch(0.97 0.01 220)" }}>Dismiss</button>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: "oklch(0.12 0.04 240)" }}>{quickActionResult.content}</p>
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msg.role === "user" ? (
                <div
                  className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-tr-sm text-sm"
                  style={{ background: "oklch(0.12 0.04 240)", color: "oklch(0.97 0.005 240)" }}
                >
                  {msg.text}
                </div>
              ) : (
                <div className="max-w-[92%] w-full">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div
                      className="w-5 h-5 rounded-md flex items-center justify-center"
                      style={{ background: "oklch(0.90 0.008 240)" }}
                    >
                      {msg.isError
                        ? <AlertCircle size={10} style={{ color: "oklch(0.08 0.01 240)" }} />
                        : <Sparkles size={10} style={{ color: "oklch(0.08 0.01 240)" }} />
                      }
                    </div>
                    <span className="text-sm font-semibold" style={{ color: "oklch(0.14 0.04 240)" }}>
                      APX Intelligence
                    </span>
                    <span className="text-sm" style={{ color: "oklch(0.14 0.04 240)" }}>
                      {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    {/* Share with Client button — only for non-error assistant messages */}
                    {!msg.isError && canShareWithClient && (
                      <button
                        onClick={() => setShareDialog({ show: true, text: msg.text })}
                        className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium transition-colors hover:bg-black/5"
                        style={{ color: "oklch(0.38 0.02 240)", border: "1px solid oklch(0.82 0.02 240)" }}
                        title="Share this summary with a client via email"
                      >
                        <Share2 size={12} />
                        Share with Client
                      </button>
                    )}
                  </div>
                  <div
                    className="rounded-xl px-4 py-3 text-sm leading-relaxed"
                    style={{
                      background: "oklch(0.95 0.006 240)",
                      border: "1px solid oklch(0.82 0.02 240)",
                      color: "oklch(0.12 0.04 240)",
                    }}
                  >
                    <Streamdown>{msg.text}</Streamdown>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Thinking indicator */}
          {queryMutation.isPending && (
            <div className="flex justify-start">
              <div className="max-w-[92%] w-full">
                <div className="flex items-center gap-2 mb-1.5">
                  <div
                    className="w-5 h-5 rounded-md flex items-center justify-center"
                    style={{ background: "oklch(0.90 0.008 240)" }}
                  >
                    <Sparkles size={10} style={{ color: "oklch(0.08 0.01 240)" }} />
                  </div>
                  <span className="text-sm font-semibold" style={{ color: "oklch(0.14 0.04 240)" }}>
                    APX Intelligence
                  </span>
                </div>
                <div
                  className="rounded-xl px-4 py-3 flex items-center gap-2"
                  style={{ background: "oklch(0.95 0.006 240)", border: "1px solid oklch(0 0 0 / 10%)" }}
                >
                  <Loader2 size={14} className="animate-spin" style={{ color: "oklch(0.08 0.01 240)" }} />
                  <span className="text-sm" style={{ color: "oklch(0.14 0.04 240)" }}>
                    Thinking...
                  </span>
                </div>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div
          className="mt-4 flex items-start gap-3 p-4 rounded-2xl"
          style={{
            background: "oklch(0.95 0.006 240)",
            border: "1px solid oklch(0 0 0 / 12%)",
          }}
        >
          <textarea
            ref={inputRef}
            rows={2}
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about APX operations, clients, P&L, or team..."
            disabled={queryMutation.isPending}
            className="flex-1 bg-transparent text-sm outline-none resize-none placeholder-[oklch(0.55_0.01_240)] disabled:opacity-50 leading-relaxed"
            style={{ color: "oklch(0.12 0.04 240)", minHeight: "52px", maxHeight: "120px" }}
          />
          <button
            onClick={() => handleSend(input)}
            disabled={!input.trim() || queryMutation.isPending}
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-150 disabled:opacity-40 mt-0.5"
            style={{
              background: "oklch(0.12 0.04 240)",
              color: "oklch(0.97 0.005 240)",
            }}
          >
            {queryMutation.isPending
              ? <Loader2 size={16} className="animate-spin" />
              : <Send size={16} />
            }
          </button>
        </div>

        {/* Live data notice — below query box */}
        <div
          className="mt-3 px-4 py-2.5 rounded-xl text-sm flex items-center gap-2"
          style={{ background: "oklch(0.93 0.008 240)", color: "oklch(0.08 0.01 240)", border: "1px solid oklch(0.82 0.02 240)" }}
        >
          <Sparkles size={12} />
          <span><strong>Live data active</strong> — APX Intelligence has access to client roster, bookkeeper assignments, and Xero P&amp;L/expense data from the Hub database (synced daily via N8N).</span>
        </div>
      </div>

      {/* Share with Client dialog */}
      {shareDialog.show && user && (
        <ShareWithClientDialog
          aiResponse={shareDialog.text}
          senderName={user.name}
          senderEmail={user.email}
          onClose={() => setShareDialog({ show: false, text: "" })}
        />
      )}
    </DashboardLayout>
  );
}
